import React, { useMemo, useState } from 'react';
import {
  format, addDays, differenceInDays, startOfWeek, endOfWeek,
  eachDayOfInterval, addWeeks, subWeeks, addMonths, subMonths,
  startOfMonth, endOfMonth,
} from 'date-fns';
import { es } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { TaskBoardTask } from '../../types';
import { PRIORITY_COLORS, ProjectPriority, TimelineMode, isTaskDone, isTaskBlocked } from '../../utils/projectConstants';

interface ProjectTimelineViewProps {
  tasks: TaskBoardTask[];
  mode: TimelineMode;
  onModeChange: (mode: TimelineMode) => void;
  onTaskClick: (task: TaskBoardTask) => void;
}

const ROW_H = 36;

const ProjectTimelineView: React.FC<ProjectTimelineViewProps> = ({
  tasks,
  mode,
  onModeChange,
  onTaskClick,
}) => {
  const [anchor, setAnchor] = useState(new Date());

  const { rangeStart, rangeEnd, days, dayWidth } = useMemo(() => {
    let start: Date, end: Date, width: number;
    if (mode === 'week') {
      start = startOfWeek(anchor, { weekStartsOn: 1 });
      end = endOfWeek(anchor, { weekStartsOn: 1 });
      width = 48;
    } else if (mode === 'month') {
      start = startOfMonth(anchor);
      end = endOfMonth(anchor);
      width = 28;
    } else {
      start = startOfMonth(anchor);
      end = addDays(start, 89);
      width = 16;
    }
    return {
      rangeStart: start,
      rangeEnd: end,
      days: eachDayOfInterval({ start, end }),
      dayWidth: width,
    };
  }, [anchor, mode]);

  const navigate = (dir: -1 | 1) => {
    if (mode === 'week') setAnchor(dir === 1 ? addWeeks(anchor, 1) : subWeeks(anchor, 1));
    else if (mode === 'month') setAnchor(dir === 1 ? addMonths(anchor, 1) : subMonths(anchor, 1));
    else setAnchor(dir === 1 ? addMonths(anchor, 3) : subMonths(anchor, 3));
  };

  const tasksWithDates = tasks.filter(t => t.start_date || t.due_date);

  const getBarStyle = (task: TaskBoardTask) => {
    const start = task.start_date ? new Date(task.start_date) : task.due_date ? new Date(task.due_date) : null;
    const end = task.due_date ? new Date(task.due_date) : start;
    if (!start || !end) return null;

    const offsetDays = Math.max(0, differenceInDays(start, rangeStart));
    const duration = Math.max(1, differenceInDays(end, start) + 1);
    const totalDays = days.length;

    if (offsetDays >= totalDays) return null;

    const left = offsetDays * dayWidth;
    const width = Math.min(duration * dayWidth, (totalDays - offsetDays) * dayWidth);

    const priority = (task.priority || 'medium') as ProjectPriority;
    let bg = PRIORITY_COLORS[priority];
    if (isTaskDone(task.status, task.completed)) bg = '#94a3b8';
    else if (isTaskBlocked(task.status)) bg = '#ef4444';
    else if (task.due_date && new Date(task.due_date) < new Date() && !isTaskDone(task.status, task.completed)) bg = '#f97316';

    return { left, width, bg };
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => navigate(-1)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><ChevronLeft className="w-4 h-4" /></button>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white capitalize">
            {format(rangeStart, 'd MMM', { locale: es })} – {format(rangeEnd, 'd MMM yyyy', { locale: es })}
          </h2>
          <button type="button" onClick={() => navigate(1)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {(['week', 'month', 'quarter'] as TimelineMode[]).map(m => (
            <button key={m} type="button" onClick={() => onModeChange(m)} className={`text-xs px-3 py-1.5 ${mode === m ? 'bg-indigo-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
              {m === 'week' ? 'Semana' : m === 'month' ? 'Mes' : 'Trimestre'}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-auto rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="flex min-w-max">
          <div className="flex-shrink-0 w-48 sticky left-0 z-10 bg-white dark:bg-gray-950 border-r border-gray-200 dark:border-gray-700">
            <div className="h-8 border-b border-gray-200 dark:border-gray-700" />
            {tasksWithDates.map(task => (
              <div key={task.id} className="h-9 flex items-center px-3 border-b border-gray-100 dark:border-gray-800">
                <button type="button" onClick={() => onTaskClick(task)} className="text-xs text-gray-800 dark:text-gray-200 truncate hover:text-indigo-600 text-left w-full">
                  {task.title}
                </button>
              </div>
            ))}
            {tasksWithDates.length === 0 && (
              <p className="text-xs text-gray-400 p-4">Agregá fechas de inicio o límite a las tareas para verlas en el timeline.</p>
            )}
          </div>

          <div className="flex-1">
            <div className="flex h-8 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-950 z-[5]">
              {days.map((day, i) => (
                <div
                  key={i}
                  style={{ width: dayWidth }}
                  className={`flex-shrink-0 text-[9px] text-center py-1 border-r border-gray-100 dark:border-gray-800 ${
                    day.getDay() === 0 || day.getDay() === 6 ? 'bg-gray-50 dark:bg-gray-900/50' : ''
                  }`}
                >
                  {mode !== 'quarter' && format(day, 'd')}
                  {mode === 'month' && i === 0 && <span className="block text-gray-400">{format(day, 'MMM', { locale: es })}</span>}
                </div>
              ))}
            </div>

            {tasksWithDates.map(task => {
              const bar = getBarStyle(task);
              return (
                <div key={task.id} className="relative h-9 border-b border-gray-100 dark:border-gray-800" style={{ width: days.length * dayWidth }}>
                  <div className="absolute inset-0 flex">
                    {days.map((day, i) => (
                      <div key={i} style={{ width: dayWidth }} className={`flex-shrink-0 border-r border-gray-50 dark:border-gray-800/50 ${day.getDay() === 0 || day.getDay() === 6 ? 'bg-gray-50/50 dark:bg-gray-900/30' : ''}`} />
                    ))}
                  </div>
                  {bar && (
                    <button
                      type="button"
                      onClick={() => onTaskClick(task)}
                      style={{ left: bar.left, width: bar.width, backgroundColor: bar.bg, top: 8, height: ROW_H - 16 }}
                      className="absolute rounded-md opacity-90 hover:opacity-100 transition-opacity px-1.5 flex items-center"
                      title={task.title}
                    >
                      <span className="text-[9px] text-white truncate font-medium">{task.title}</span>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectTimelineView;
